import { useAccessParams } from "auth"
import { logoutAndLogin } from "auth/src/login"
import { invalidateAllQueries } from "bonzai"
import { useGraphqlQuery } from "graphql-mock"
import { useModal, useQueryParam } from "hooks"
import { Dialog, Loader } from "materia"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { withQueryParams } from "utils"
import { ATTENSI_PRODUCT_PLATFORM_FOR_OS } from "../../constants"
import { getVideoLink } from "../../dataUtilities/getVideoLink"
import {
  getFileDownloadLink,
  getLocalizedPDFLink,
  getPreferredContentLink,
} from "../../dataUtilities/productDataUtilities"
import { createPlaythrough } from "../../graphql/createPlaythrough"
import { useIframeMessages } from "../../tools/useIframeMessages"
import { usePageTitle } from "../../tools/usePageTitle"
import {
  pollForProgressChange,
  snapshotProgressBefore,
} from "../ConfettiBanner/checkForProgressChange"
import { useBlockConfettiBanner } from "../ConfettiBanner/ConfettiContextProvider"
import { IS_APP } from "../../vuplex/constants"
import { PDFViewer } from "../PDFViewer"
import { useSharedDeviceStore } from "../SharedDeviceInactivityTracker"
import { ConfirmationModal } from "./components/ConfirmationModal"
import { Iframe } from "./components/Iframe"
import { ProductViewModal } from "./components/ProductViewModal"
import { VideoPlayer } from "./components/VideoPlayer"
import { ViewerModalError } from "./components/ViewerModalError"
import { query, type Data } from "./gql"
import {
  useAutoRotation,
  useRealTalkStreamLink,
  useRealTalkWebglLink,
  useScormLink,
  useWebglLink,
} from "./hooks"
import s from "./ViewerModal.module.scss"

// This is a "meta" component
// It's mainly here to fetch and find out which product which type of product we are working with.
// When we know which product, we hand over the responsibility of handling it to the relevant product viewer.
// It's currently coupled with providing the Dialog (background) for the modal, if it becomes an issue we should decouple.
export const ViewerModal = ({
  closeRoute,
  productId,
}: {
  closeRoute: string
  productId: string
}) => {
  useBlockConfettiBanner()

  const { onClose } = useModal(closeRoute)

  const [locale] = useQueryParam("content_locale")

  const { data, error, isFetchedAfterMount, isPending } = useGraphqlQuery(
    query,
    {
      productId,
      platform: ATTENSI_PRODUCT_PLATFORM_FOR_OS,
    },
    {
      // If a direct link to S3 is used instead of the redirect one,
      // a new link will be generated on every window refocus
      // and entire modal will re-render unnecessarily.
      refetchOnWindowFocus: false,
    }
  )

  usePageTitle(data?.product.title)

  // Snapshot the product's progress at open time (before the user plays) so the
  // completion poll can diff against it. No extra network call — the modal query
  // already carries this progress.
  useEffect(() => {
    if (data) snapshotProgressBefore(productId, data.product.progress)
  }, [data, productId])

  /**
   * On close we always refresh queries (a plain close should reflect any state
   * change) and start a bounded poll that picks up the backend's async score
   * processing and hands it to the confetti/diploma path.
   */
  const handleClose = () => {
    invalidateAllQueries()
    pollForProgressChange(productId)
    onClose()
  }

  // Using isFetchedAfterMount here ensures that we never use stale data.
  // This is useful in case of one-time-use direct S3 links (used by PDFs)
  const isLoading = isPending || !isFetchedAfterMount

  if (error) return <ViewerModalError error={error} onClose={handleClose} />

  return (
    <Dialog ariaLabel="Product content" onClose={handleClose}>
      <div className={s.content}>
        {isLoading ? (
          <Loader />
        ) : (
          <ProductSwitcher data={data} onClose={handleClose} locale={locale} />
        )}
      </div>
    </Dialog>
  )
}

// This is a "meta" component
// It's only a wrapper for rendering the correct viewer modal for each product
// The different viewer modals has the responsibility of making their products appear a certain way,
// acquiring the correct URL and applying any special logic that might apply to their product.
const ProductSwitcher = ({
  data,
  onClose,
  locale,
}: {
  data: Data
  onClose: () => void
  locale: string | undefined
}) => {
  const {
    id,
    contentLink,
    playLinks,
    productType,
    productUploads,
    title,
    useTransferToken,
  } = data.product

  switch (productType) {
    case "skill":
      return (
        <SkillsViewer
          title={title}
          productId={id}
          webglLink={playLinks.webglLink}
          useTransferToken={useTransferToken}
          onClose={onClose}
        />
      )
    case "behavior":
    case "operations":
    case "process":
    case "workflow":
    case "act":
      return (
        <WebGLViewer
          title={title}
          productId={id}
          webglLink={playLinks.webglLink}
          useTransferToken={useTransferToken}
          onClose={onClose}
        />
      )
    case "pdf":
      return (
        <PDFFileViewer
          title={title}
          productId={id}
          contentLink={contentLink}
          uploads={productUploads.nodes}
          locale={locale}
          onClose={onClose}
        />
      )
    case "video":
      return <VideoViewer data={data} locale={locale} onClose={onClose} />
    case "xapi":
      return (
        <XapiViewer title={title} contentLink={contentLink} onClose={onClose} />
      )
    case "scorm":
      return <ScormViewer title={title} productId={id} onClose={onClose} />
    case "fast":
    case "quiz_race":
    case "process_on_web":
    case "fast_survey":
    case "dialogue":
    case "skills_real_talk":
      return (
        <WebGamesViewer
          title={title}
          contentLink={contentLink}
          onClose={onClose}
        />
      )
    case "assessment": // Also called exam
      return (
        <ExamViewer title={title} contentLink={contentLink} onClose={onClose} />
      )
    case "real_talk":
    case "real_talk_maker":
      return (
        <RealTalkViewer
          playLinks={playLinks}
          productId={id}
          title={title}
          onClose={onClose}
        />
      )
  }

  throw new Error("Cannot view this product type")
}

const PDFFileViewer = ({
  title,
  productId,
  contentLink,
  uploads,
  onClose,
  locale,
}: {
  title: string | null
  productId: string
  contentLink: string
  uploads: {
    locale: string
    url: string
    downloadUrl: string
  }[]
  onClose: () => void
  locale: string | undefined
}) => {
  const fileUrl = getLocalizedPDFLink({ uploads, locale, contentLink })
  const downloadUrl = getFileDownloadLink({ uploads, locale })

  return (
    <ProductViewModal title={title} view="fullScreen" onClose={onClose}>
      <PDFViewer
        fileUrl={fileUrl}
        downloadUrl={downloadUrl}
        productId={productId}
        title={title ?? ""}
      />
    </ProductViewModal>
  )
}

const RealTalkViewer = ({
  playLinks,
  productId,
  title,
  onClose,
}: {
  // TODO [api-decoupling] Replace with type not tied to Data
  playLinks: Data["product"]["playLinks"]
  productId: Data["product"]["id"]
  title: Data["product"]["title"]
  onClose: () => void
}) => {
  useAutoRotation()
  usePlayAttensiProduct()

  const { i18n } = useTranslation()
  const { streamLink, webglLink } = playLinks

  // Both RealTalk targets always require a transfer token, also inside Skills+.
  const {
    data: streamLinkWithParams,
    isPending: isStreamPending,
    error: streamError,
  } = useRealTalkStreamLink(productId, streamLink)
  const {
    data: webglLinkWithParams,
    isPending: isWebglPending,
    error: webglError,
  } = useRealTalkWebglLink(productId, webglLink)

  // The stream (ShortsWeb) has no mobile UI, so mobile / Skills+ always gets
  // the web app. This mobile check must match RealTalkProductButtons.
  const mobile =
    IS_APP ||
    ATTENSI_PRODUCT_PLATFORM_FOR_OS === "iOS" ||
    ATTENSI_PRODUCT_PLATFORM_FOR_OS === "Android"
  const preferWebgl = (mobile && Boolean(webglLink)) || !streamLink
  const src = preferWebgl ? webglLinkWithParams : streamLinkWithParams
  const isPending = preferWebgl ? isWebglPending : isStreamPending
  const error = preferWebgl ? webglError : streamError

  const productLink = withQueryParams(src ?? "", { locale: i18n.language })

  return (
    <ProductViewModal
      isLoading={isPending}
      view="fullScreen"
      title={title}
      onClose={onClose}
      error={error}
    >
      <Iframe src={productLink} permissions={["microphone"]} />
    </ProductViewModal>
  )
}

// TODO: Clean up and get the dependencies cleaned up and decoupled
const VideoViewer = ({
  data,
  onClose,
  locale,
}: {
  data: Data
  onClose: () => void
  locale: string | undefined
}) => {
  useAutoRotation()

  const isPlaythroughCreated = useRef(false)
  const { product, currentCompany } = data
  const { localeContentLinks, title, contentLink } = data.product

  const isSkipForwardDisabled =
    currentCompany.companyFeatureFlags.includes("enable_no_skip_videos") &&
    product.noSkip &&
    !product.progress.certified

  const link = getPreferredContentLink({
    localeContentLinks,
    locale,
    contentLink,
  })

  const videoLink = getVideoLink(link)

  // [TODO] https://jira.attensi.com/browse/WEB-19318
  // This is a fix for video playthroughs not being created correctly.
  // With the ProductModal refactor, playthrough creation should be handled
  // in a more centralized way regardless of product type.
  const onPlaythroughComplete = useCallback(() => {
    if (!isPlaythroughCreated.current) {
      isPlaythroughCreated.current = true
      createPlaythrough(product.id)
      // product.id is the graphql-returned id and equals the `productId` prop
      // handleClose/snapshotProgressBefore use (the query variable), so this
      // polls the same productProgressMap entry. Single-flight in the poll module
      // means this won't double up with the poll started by handleClose.
      pollForProgressChange(product.id)
    }
  }, [product.id])

  return (
    <ProductViewModal title={title} onClose={onClose} view="fullScreen">
      <VideoPlayer
        src={videoLink}
        isSkipForwardDisabled={isSkipForwardDisabled}
        onPlaythroughComplete={onPlaythroughComplete}
      />
    </ProductViewModal>
  )
}

const WebGamesViewer = ({
  title,
  contentLink,
  onClose,
}: {
  title: string | null
  contentLink: string
  onClose: () => void
}) => {
  const { i18n } = useTranslation()

  useIframeMessages({
    targetUrl: contentLink,
    onMessage: ({ type }) => {
      if (type === "EXIT_MODAL") onClose()
    },
  })

  const url = withQueryParams(contentLink, { locale: i18n.language })
  const { link, isPending, error } = useAccessParams(url)

  return (
    <ProductViewModal
      title={title}
      onClose={onClose}
      error={error}
      isLoading={isPending}
      view="fullScreen"
    >
      <Iframe src={link} permissions={["microphone"]} />
    </ProductViewModal>
  )
}

const ExamViewer = ({
  title,
  contentLink,
  onClose,
}: {
  title: string | null
  contentLink: string
  onClose: () => void
}) => {
  const { i18n, t } = useTranslation()

  useIframeMessages({
    targetUrl: contentLink,
    onMessage: ({ type }) => {
      if (type === "EXIT_MODAL") onClose()
    },
  })

  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false)
  const url = withQueryParams(contentLink, { locale: i18n.language })
  const { link, isPending, error } = useAccessParams(url)

  return (
    <>
      {showConfirmCloseModal && (
        <ConfirmationModal
          title={t("confirmCloseModal.QUIT_GAME")}
          description={t("confirmCloseModal.QUIT_EXAM_DESCRIPTION")}
          confirmButtonText={t("actions.QUIT")}
          onConfirm={() => {
            setShowConfirmCloseModal(false)
            onClose()
          }}
          onCancel={() => setShowConfirmCloseModal(false)}
        />
      )}

      <ProductViewModal
        title={title}
        onClose={() => {
          setShowConfirmCloseModal(true)
        }}
        error={error}
        isLoading={isPending}
        view="fullScreen"
      >
        <Iframe src={link} />
      </ProductViewModal>
    </>
  )
}

const XapiViewer = ({
  title,
  contentLink,
  onClose,
}: {
  title: string | null
  contentLink: string
  onClose: () => void
}) => {
  useAutoRotation()

  const { link, isPending, error } = useAccessParams(contentLink)

  return (
    <ProductViewModal
      title={title}
      onClose={onClose}
      isLoading={isPending}
      error={error}
      view="fullScreen"
    >
      <Iframe src={link} />
    </ProductViewModal>
  )
}

const ScormViewer = ({
  title,
  productId,
  onClose,
}: {
  title: string | null
  productId: string
  onClose: () => void
}) => {
  useAutoRotation()

  const { data: link, isPending, error } = useScormLink(productId)

  return (
    <ProductViewModal
      title={title}
      onClose={onClose}
      isLoading={isPending}
      error={error}
      view="fullScreen"
    >
      <Iframe src={link} />
    </ProductViewModal>
  )
}

// TODO: remove portrait related styles
const WebGLViewer = ({
  title,
  productId,
  webglLink,
  useTransferToken,
  onClose,
}: {
  title: string | null
  productId: string
  webglLink: string | null
  useTransferToken: boolean
  onClose: () => void
}) => {
  const {
    data: src,
    isPending,
    error,
  } = useWebglLink(productId, webglLink, useTransferToken)

  usePlayAttensiProduct()

  return (
    <ProductViewModal
      title={title}
      onClose={onClose}
      error={error}
      isLoading={isPending}
      isFramed={true}
      view="landscape"
    >
      <Iframe src={src} permissions={["microphone"]} />
    </ProductViewModal>
  )
}

const SkillsViewer = ({
  title,
  productId,
  webglLink,
  useTransferToken,
  onClose,
}: {
  title: string | null
  productId: string
  webglLink: string | null
  useTransferToken: boolean
  onClose: () => void
}) => {
  // const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false)
  const {
    data: src,
    isPending,
    error,
  } = useWebglLink(productId, webglLink, useTransferToken)
  const { i18n } = useTranslation()

  usePlayAttensiProduct()

  // Skills will transition all their products, over time, to use full screen.
  // We can remove the query param and condition, once its done
  const urlQuery = webglLink?.split("?")[1]
  const view = urlQuery?.includes("fullscreen=false")
    ? "portrait"
    : "fullScreen"

  const { isSharedDevice } = useSharedDeviceStore()

  const srcWithQueryParam = withQueryParams(src || "", {
    shared_device: isSharedDevice,
    locale: i18n.language, // this will be user's currently selected content locale
    embed_source: "engage",
  })

  useIframeMessages({
    targetUrl: srcWithQueryParam,
    onMessage: ({ type }) => {
      if (type === "LOGOUT") logoutAndLogin()
      if (type === "EXIT_MODAL") onClose()
    },
  })

  return (
    <>
      {/*{showConfirmCloseModal && (*/}
      {/*  <ConfirmationModal*/}
      {/*    title={t("confirmCloseModal.QUIT_GAME")}*/}
      {/*    description={t("confirmCloseModal.QUIT_SKILLS_DESCRIPTION")}*/}
      {/*    confirmButtonText={t("actions.QUIT")}*/}
      {/*    onConfirm={() => {*/}
      {/*      setShowConfirmCloseModal(false)*/}
      {/*      onClose()*/}
      {/*    }}*/}
      {/*    onCancel={() => setShowConfirmCloseModal(false)}*/}
      {/*  />*/}
      {/*)}*/}

      <ProductViewModal
        title={title}
        isLoading={isPending}
        error={error}
        onClose={() => {
          // setShowConfirmCloseModal(true) - re-enable and remove onClose below when enabling this
          onClose()
        }}
        view={view}
        isFramed={view !== "fullScreen"}
      >
        <Iframe src={srcWithQueryParam} />
      </ProductViewModal>
    </>
  )
}

const usePlayAttensiProduct = () => {
  const { setIsPlayingAttensiProduct } = useSharedDeviceStore()

  useEffect(() => {
    setIsPlayingAttensiProduct(true)
    return () => {
      setIsPlayingAttensiProduct(false)
    }
  }, [setIsPlayingAttensiProduct])

  return {}
}
